// Svelte 5 state runes are available globally, no import needed
import { resetStudioSettingsUI, studioSettingsUI } from './settings.svelte.js';
import { clearImageReferences } from './image-references.svelte.js';
import { restoreGenerationRun } from './generation-run.svelte.js';

// Main session state
export const session = $state({
  id: crypto.randomUUID(),
  startedAt: Date.now(),
  creativeBrief: null,
  wizardDraft: null,
  wizardProgress: null,
  branches: [],
  currentBranchId: null,
  currentIterationIndex: 0,
  phase: 'wizard', // 'wizard' | 'visualizer' | 'confirmation' | 'studio' | 'finalized'
  hasSubmittedInitialPrompt: false,
  visualizerSource: null, // 'wizard' | 'studio'
  finalImageUrl: null,
  finalImagePrompt: null,
  finalImageSensitive: false,
  finalImageProjectId: null,
  // Confirmation phase data
  confirmationData: null // { combinations, stats }
});

// Prompt structure for visualizer
export const promptStructure = $state({
  components: [],  // [{id, type, label, value, position, isFaceted, facetId}]
  connections: [], // [{source, target}]
  facets: []       // [{id, componentId, directive, variants: []}]
});

/** Install detached data after its account-scoped assets have been hydrated. */
export function restoreCreativeSession(snapshot) {
  for (const key of Object.keys(session)) {
    if (key in snapshot.session) session[key] = snapshot.session[key];
  }
  session.wizardDraft = snapshot.session.wizardDraft ?? null;
  session.wizardProgress = snapshot.session.wizardProgress ?? null;
  promptStructure.components = snapshot.promptStructure?.components || [];
  promptStructure.connections = snapshot.promptStructure?.connections || [];
  promptStructure.facets = snapshot.promptStructure?.facets || [];
  resetStudioSettingsUI();
  // An empty restored branch never starts a paid or covered batch on reload.
  studioSettingsUI.pauseInitialGeneration = true;
}

// Maximum number of facets allowed
export const MAX_FACETS = 3;

// Computed statistics - export as object with getter functions
export const stats = {
  get totalIterations() {
    return session.branches.reduce((sum, b) => sum + b.iterations.length, 0);
  },
  get totalBranches() {
    return session.branches.length;
  },
  get totalImages() {
    return session.branches.reduce((branchSum, branch) => {
      const branchTotal = branch.iterations.reduce((iterationSum, iteration) => {
        if (!iteration || !Array.isArray(iteration.batches)) {
          return iterationSum;
        }
        const iterationImages = iteration.batches.reduce((batchSum, batch) => {
          if (!Array.isArray(batch)) {
            return batchSum;
          }
          return batchSum + batch.length;
        }, 0);
        return iterationSum + iterationImages;
      }, 0);
      return branchSum + branchTotal;
    }, 0);
  },
  get duration() {
    return Date.now() - session.startedAt;
  },
  get durationFormatted() {
    const ms = this.duration;
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  },
  get timeFormatted() {
    return new Date(session.startedAt).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
};

// Get current branch - export as object with getter
export const currentBranch = {
  get value() {
    return session.branches.find(b => b.id === session.currentBranchId);
  }
};

// Get current iteration - export as object with getter
export const currentIteration = {
  get value() {
    const branch = currentBranch.value;
    return branch ? branch.iterations[session.currentIterationIndex] : null;
  }
};

// Get selected images from current iteration - export as object with getter
export const selectedImages = {
  get value() {
    const iteration = currentIteration.value;
    if (!iteration) return [];

    // Flatten with batchIndex and imageIndex preserved
    const selected = [];
    iteration.batches.forEach((batch, batchIndex) => {
      batch.forEach((img, imageIndex) => {
        if (img.selected) {
          selected.push({
            ...img,
            batchIndex,
            imageIndex
          });
        }
      });
    });
    return selected;
  }
};

// Navigation state - export as object with getters
export const navigation = {
  get canGoBack() {
    return session.currentIterationIndex > 0;
  },
  get canGoForward() {
    const branch = currentBranch.value;
    return branch && session.currentIterationIndex < branch.iterations.length - 1;
  },
  get isFirstIteration() {
    return session.currentIterationIndex === 0;
  },
  get isLastIteration() {
    const branch = currentBranch.value;
    return branch && session.currentIterationIndex === branch.iterations.length - 1;
  }
};

// Phase 1 -> Phase 2 transition
export function startStudio(brief) {
  session.creativeBrief = brief;

  // Reset UI state BEFORE changing phase (to avoid effect race conditions)
  resetStudioSettingsUI();

  // Pause the first batch so the studio can offer the render setup modal
  // (models + image size) before anything renders. Signed-out users are asked
  // to sign in first; rendering needs their own account.
  studioSettingsUI.pauseInitialGeneration = true;

  // Now change phase (this triggers Studio's effects)
  session.phase = 'studio';
  session.visualizerSource = null;

  const firstBranch = {
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    iterations: []
  };

  session.branches = [firstBranch];
  session.currentBranchId = firstBranch.id;
}

/** Open a reviewed favorite edit without replacing the user's existing work. */
export function startFavoriteStudio(brief, favoriteId) {
  const branch = {
    id: crypto.randomUUID(), createdAt: Date.now(), sourceFavoriteId: favoriteId,
    parentBranchId: session.currentBranchId, iterations: []
  };
  resetStudioSettingsUI();
  studioSettingsUI.pauseInitialGeneration = true;
  session.creativeBrief = brief;
  session.branches = [...session.branches, branch];
  session.currentBranchId = branch.id;
  session.currentIterationIndex = 0;
  session.visualizerSource = null;
  clearFinalImage();
  session.confirmationData = null;
  session.phase = 'studio';
}

// Transition to confirmation phase (before studio)
export function startConfirmation(brief, combinations, stats) {
  session.creativeBrief = brief;
  session.confirmationData = {
    combinations,
    stats
  };
  session.phase = 'confirmation';
}

// Go back from confirmation to wizard
export function backToWizard() {
  session.phase = 'wizard';
  session.confirmationData = null;
}

// Add new iteration to current branch
export function addIteration(iteration) {
  let branch = session.branches.find(b => b.id === session.currentBranchId);
  if (!branch) {
    const fallbackBranch = {
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      iterations: []
    };
    session.branches = session.branches.length > 0
      ? [...session.branches, fallbackBranch]
      : [fallbackBranch];
    session.currentBranchId = fallbackBranch.id;
    branch = fallbackBranch;
  }

  // If we're not at the end, we need to create a branch
  if (session.currentIterationIndex < branch.iterations.length - 1) {
    createBranch(branch.iterations[session.currentIterationIndex].id);
    const newBranch = session.branches.find(b => b.id === session.currentBranchId);
    newBranch.iterations.push(iteration);
    session.currentIterationIndex = 0;
  } else {
    branch.iterations.push(iteration);
    session.currentIterationIndex = branch.iterations.length - 1;
  }
}

// Create new branch (for multi-select merges or when going back)
export function createBranch(fromIterationId) {
  const currentBranchObj = currentBranch.value;
  const parentIteration = currentBranchObj.iterations.find(i => i.id === fromIterationId);

  const newBranch = {
    id: crypto.randomUUID(),
    parentBranchId: session.currentBranchId,
    parentIterationId: fromIterationId,
    createdAt: Date.now(),
    iterations: parentIteration ? [parentIteration] : []
  };

  session.branches.push(newBranch);
  session.currentBranchId = newBranch.id;
  session.currentIterationIndex = 0;
}

// Navigation functions
export function goBack() {
  if (navigation.canGoBack) {
    session.currentIterationIndex--;
  }
}

export function goForward() {
  if (navigation.canGoForward) {
    session.currentIterationIndex++;
  }
}

// Toggle image selection
export function toggleImageSelection(batchIndex, imageIndex) {
  const iteration = currentIteration.value;
  if (!iteration) return;

  const image = iteration.batches?.[batchIndex]?.[imageIndex];
  if (!image) return;
  image.selected = !image.selected;
}

export function deleteImageFromIteration(batchIndex, imageIndex) {
  const iteration = currentIteration.value;
  if (!iteration) return;

  const batch = iteration.batches?.[batchIndex];
  if (!batch || !batch[imageIndex]) return;

  batch.splice(imageIndex, 1);
}

export function setImageRating(batchIndex, imageIndex, rating) {
  const iteration = currentIteration.value;
  if (!iteration) return;

  const image = iteration.batches?.[batchIndex]?.[imageIndex];
  if (!image) return;

  image.rating = rating;
}

// Clear all selections
export function clearSelections() {
  const iteration = currentIteration.value;
  if (!iteration) return;

  iteration.batches.forEach(batch => {
    batch.forEach(img => {
      img.selected = false;
    });
  });
}

// Phase 2 -> Phase 3 transition. The chosen image's sensitive label and
// project id travel with it so the final view covers it like every other view.
export function finalize(image) {
  session.finalImageUrl = image.url;
  session.finalImagePrompt = image.prompt;
  session.finalImageSensitive = image.sensitive === true;
  session.finalImageProjectId = image.projectId || null;
  session.phase = 'finalized';
}

function clearFinalImage() {
  session.finalImageUrl = null;
  session.finalImagePrompt = null;
  session.finalImageSensitive = false;
  session.finalImageProjectId = null;
}

// Back to studio from finalized view
export function backToStudio() {
  // Update URL with history for proper back/forward navigation
  history.pushState({ phase: 'studio' }, '', '/studio');
  session.phase = 'studio';
  clearFinalImage();
}

// Restart entire session
export function restart() {
  clearImageReferences();
  clearPromptStructure();
  restoreGenerationRun(null);
  session.id = crypto.randomUUID();
  session.startedAt = Date.now();
  session.creativeBrief = null;
  session.wizardDraft = null;
  session.wizardProgress = null;
  session.branches = [];
  session.currentBranchId = null;
  session.currentIterationIndex = 0;
  session.phase = 'wizard';
  session.hasSubmittedInitialPrompt = false;
  clearFinalImage();
  session.confirmationData = null;

  resetStudioSettingsUI();
}

export function markInitialPromptSubmitted() {
  session.hasSubmittedInitialPrompt = true;
}

// Update prompt for current iteration
export function updateCurrentPrompt(newPrompt) {
  const iteration = currentIteration.value;
  if (iteration) {
    iteration.prompt = newPrompt;
    iteration.userEdited = true;
  }
}

// Get batch weights for multi-select (when multiple images from same batch selected)
export function getBatchWeights() {
  const selected = selectedImages.value;
  const batchCounts = {};

  selected.forEach(img => {
    batchCounts[img.batchId] = (batchCounts[img.batchId] || 0) + 1;
  });

  const weights = {};
  Object.keys(batchCounts).forEach(batchId => {
    if (batchCounts[batchId] > 1) {
      // Weight more heavily if multiple from same batch (1.5x per additional selection)
      weights[batchId] = 1 + (batchCounts[batchId] - 1) * 0.5;
    } else {
      weights[batchId] = 1;
    }
  });

  return weights;
}

// ========== Prompt Visualizer Functions ==========

// Set prompt structure from decomposition API response
export function setPromptStructure(components, connections) {
  promptStructure.components = components;
  promptStructure.connections = connections;
  promptStructure.facets = [];
}

// Update a component's value
export function updateComponent(componentId, newValue) {
  const component = promptStructure.components.find(c => c.id === componentId);
  if (component) {
    component.value = newValue;
    component.isEmpty = newValue.trim() === '';
  }
}

// Add a facet to a component
export function addFacet(componentId, directive, variants) {
  if (promptStructure.facets.length >= MAX_FACETS) {
    console.warn('Maximum facets reached');
    return null;
  }

  const facetId = `facet-${crypto.randomUUID().slice(0, 8)}`;
  const facet = {
    id: facetId,
    componentId,
    directive,
    variants
  };

  promptStructure.facets.push(facet);

  // Mark component as faceted
  const component = promptStructure.components.find(c => c.id === componentId);
  if (component) {
    component.isFaceted = true;
    component.facetId = facetId;
  }

  return facetId;
}

// Remove a facet from a component
export function removeFacet(facetId) {
  const facetIndex = promptStructure.facets.findIndex(f => f.id === facetId);
  if (facetIndex === -1) return;

  const facet = promptStructure.facets[facetIndex];

  // Unmark component
  const component = promptStructure.components.find(c => c.id === facet.componentId);
  if (component) {
    component.isFaceted = false;
    component.facetId = null;
  }

  // Remove facet
  promptStructure.facets.splice(facetIndex, 1);
}

// Get facet for a component
export function getFacetForComponent(componentId) {
  return promptStructure.facets.find(f => f.componentId === componentId);
}

// Update a single facet variant text
export function updateFacetVariant(componentId, variantIndex, newValue) {
  const facet = promptStructure.facets.find(f => f.componentId === componentId);
  if (facet && facet.variants[variantIndex] !== undefined) {
    facet.variants[variantIndex] = newValue;
  }
}

// Delete a single facet variant (must have at least 1 remaining)
export function deleteFacetVariant(componentId, variantIndex) {
  const facet = promptStructure.facets.find(f => f.componentId === componentId);
  if (!facet) return;

  if (facet.variants.length > 1) {
    facet.variants.splice(variantIndex, 1);
  } else if (facet.variants.length === 1) {
    // If deleting last variant, remove the entire facet
    removeFacet(facet.id);
  }
}

// Delete a component
export function deleteComponent(componentId) {
  // Prevent deleting the last component
  if (promptStructure.components.length <= 1) {
    return false; // Cannot delete
  }

  // Remove any facet for this component
  const facet = promptStructure.facets.find(f => f.componentId === componentId);
  if (facet) {
    promptStructure.facets = promptStructure.facets.filter(f => f.id !== facet.id);
  }

  // Remove the component
  const component = promptStructure.components.find(c => c.id === componentId);
  if (component) {
    component.isFaceted = false;
    component.facetId = null;
  }
  promptStructure.components = promptStructure.components.filter(c => c.id !== componentId);

  // Remove any connections involving this component
  promptStructure.connections = promptStructure.connections.filter(
    conn => conn.source !== componentId && conn.target !== componentId
  );

  return true; // Successfully deleted
}

// Clear prompt structure
export function clearPromptStructure() {
  promptStructure.components = [];
  promptStructure.connections = [];
  promptStructure.facets = [];
}

// Pillar-to-component type mapping for wizard facets
const PILLAR_TO_COMPONENT_TYPES = {
  'subject_action': ['subject', 'action'],
  'environment_mood': ['mood', 'setting'],
  'composition_camera': ['composition'],
  'style': ['style', 'lighting', 'colors', 'details']
};

// Extract wizard facets from multi-select fields
export function extractWizardFacets(fields) {
  if (!fields || !Array.isArray(fields)) return [];

  return fields
    .filter(f => f.isFaceted && f.values && f.values.length > 1)
    .map(f => ({
      pillar: f.pillar,
      fieldName: f.name,
      variants: f.values
    }));
}

// Apply wizard facets to prompt structure components
export function applyWizardFacetsToStructure(wizardFacets) {
  if (!wizardFacets || wizardFacets.length === 0) return;

  for (const wFacet of wizardFacets) {
    // Skip if max facets reached
    if (promptStructure.facets.length >= MAX_FACETS) break;

    const targetTypes = PILLAR_TO_COMPONENT_TYPES[wFacet.pillar] || [];

    // Find the first matching non-faceted component
    const targetComponent = promptStructure.components.find(
      c => targetTypes.includes(c.type.toLowerCase()) && !c.isFaceted
    );

    if (targetComponent) {
      // Add facet with wizard directive prefix
      addFacet(
        targetComponent.id,
        `wizard:${wFacet.fieldName}`,
        wFacet.variants
      );
    }
  }
}

// Transition to visualizer phase
export function startVisualizer(brief, options = {}) {
  // Extract wizard facets from multi-select fields
  const wizardFacets = extractWizardFacets(brief.fields || []);

  session.creativeBrief = {
    ...brief,
    wizardFacets
  };
  session.visualizerSource = options.source || 'wizard';
  session.phase = 'visualizer';
}

// Skip visualizer and go to studio
export function skipVisualizer() {
  session.phase = 'studio';
  session.visualizerSource = null;
  clearPromptStructure();
}

// Return to studio without resetting iterations/branches
export function returnToStudio(brief) {
  if (brief) {
    session.creativeBrief = brief;
  }
  session.phase = 'studio';
  session.visualizerSource = null;
}
